import { create, useModal } from '@ebay/nice-modal-react';
import Avatar from '@shared/components/Avatar/Avatar';
import Button from '@shared/components/Button/Button';
import Divider from '@shared/components/Divider/Divider';
import Popup from '@shared/components/Popup/Popup';
import RadioButton from '@shared/components/RadioButton/RadioButton';
import { Room } from 'matrix-js-sdk/src/matrix';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { updateMeeting } from 'api/meedioBackend/updateMeeting';
import { ReactComponent as NewConversationIcon } from 'assets/icons/NewConversation.svg';
import CloseButton from 'components/CloseButton';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import useToast from 'contexts/ToastProvider/useToast';
import useMessengerRoomCreation from 'hooks/roomCreation/useMessengerRoomCreation';
import useRoomAccessInitialState from 'hooks/roomCreation/useRoomAccessInitialState';
import useRoomCreationPowerLevels from 'hooks/roomCreation/useRoomCreationPowerLevels';
import useRoomManagersInitialState from 'hooks/roomCreation/useRoomManagersInitialState';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import retryPromise from 'utils/retryPromise';

import { is409AliasTaken } from './useScheduleMeetingSubmit';

interface GroupChatConflictModalProps {
  meetingId: string;
  conflicts: Room[];
  participantMxids: string[];
}

const GroupChatConflictModalInner = ({ meetingId, conflicts, participantMxids }: GroupChatConflictModalProps) => {
  const { t } = useTranslation();
  const modal = useModal();
  const { pushToast } = useToast();
  const { workspaceId } = useWorkspacesContext();
  const { createMessengerRoom } = useMessengerRoomCreation();
  const roomManagersInitialState = useRoomManagersInitialState(workspaceId);
  const teamRoomAccessInitialState = useRoomAccessInitialState('team');
  // BUG-003: the created room is a messenger group chat → chat-scoped power levels (mirrors /messenger).
  const { powerLevelOverrides: chatPowerLevelOverrides, ownerPowerLevel: chatOwnerPowerLevel } =
    useRoomCreationPowerLevels('chat');
  const [selectedRoomId, setSelectedRoomId] = useState<string>(conflicts[0]?.roomId ?? '');
  const [isLoading, setIsLoading] = useState(false);

  if (conflicts.length === 0) return null;

  const handleClose = () => {
    modal.resolve({ action: 'close' });
    modal.remove();
  };

  const retryOpts = {
    retries: 3,
    delayMs: (n: number) => 500 * 2 ** n,
    shouldRetry: (err: unknown) => !is409AliasTaken(err),
  };

  const handleUseExisting = async () => {
    setIsLoading(true);
    try {
      await retryPromise({
        fn: () => updateMeeting(meetingId, { messenger_room_id: selectedRoomId }),
        ...retryOpts,
        label: 'conflict-updateMeeting',
      });
      modal.resolve({ action: 'use-existing', roomId: selectedRoomId });
      modal.remove();
    } catch {
      pushToast({ variant: 'error', title: t('schedule_meeting.submit_failed_retry_later') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = async () => {
    setIsLoading(true);
    try {
      const newRoom = await retryPromise({
        fn: () =>
          createMessengerRoom({
            participantMxids,
            workspaceId,
            ...(workspaceId
              ? {
                  roomManagersInitialState,
                  roomAccessInitialState: teamRoomAccessInitialState,
                  powerLevelOverrides: chatPowerLevelOverrides,
                  ownerPowerLevel: chatOwnerPowerLevel,
                }
              : {}),
          }),
        ...retryOpts,
        label: 'conflict-createMessengerRoom',
      });
      await retryPromise({
        fn: () => updateMeeting(meetingId, { messenger_room_id: newRoom.roomId }),
        ...retryOpts,
        label: 'conflict-updateMeeting-new',
      });
      modal.resolve({ action: 'create-new', roomId: newRoom.roomId });
      modal.remove();
    } catch {
      pushToast({ variant: 'error', title: t('schedule_meeting.submit_failed_retry_later') });
    } finally {
      setIsLoading(false);
    }
  };

  const isSingle = conflicts.length === 1;

  // BUG-043: md:!w-full overrides Popup inner div's md:w-100 (400px); px-4/md:px-0 gives 16px outer gap on mobile (BUG-044)
  return (
    <Popup isVisible closePopup={handleClose} className="md:!w-full px-4 md:px-0">
      {/* BUG-038: Popup.Container (h-auto rounded-2xl) keeps the dialog floating on all screen sizes */}
      {/* BUG-043: !max-w-[512px] overrides popupContainerWidthStyle's md:max-w-100 (400px) via !important */}
      <Popup.Container className="relative flex min-h-0 flex-col border border-gray-30 !bg-gray-10 !max-w-[512px]">
        <CloseButton onClick={handleClose} className="absolute right-[15px] top-[15px] z-10 !bg-gray-10" />
        <div className="flex flex-col items-center gap-8 px-4 pb-6 pt-12 text-center md:px-6">
          {/* BUG-039: bgVariant="primary" → bg-primary-10 (#E6F2F0); iconVariant="primary" → stroke-primary-50 (#007965) */}
          <GradientIconWithShadow
            icon={NewConversationIcon}
            bgVariant="primary"
            iconVariant="primary"
            iconClassName="stroke-1.5"
            className="backdrop-blur-[20px]"
          />
          <div className="flex w-full flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h2 className="text-xl font-semibold leading-[1.25] text-black">
                {t('schedule_meeting.conflict_title')}
              </h2>
              <p className="text-base font-light text-gray-80">
                {isSingle ? (
                  <Trans i18nKey="schedule_meeting.conflict_body_single" values={{ name: conflicts[0].name }} />
                ) : (
                  t('schedule_meeting.conflict_body_multiple')
                )}
              </p>
            </div>
            {!isSingle && (
              <div className="flex max-h-48 w-full flex-col overflow-y-auto rounded-xl border border-gray-30 bg-white p-4">
                {conflicts.map((room, index) => (
                  <div key={room.roomId}>
                    {index > 0 && <Divider />}
                    <label className="flex cursor-pointer items-center gap-2 py-2">
                      <Avatar name={room.name} size="sm" />
                      {/* BUG-037: text-left overrides inherited text-center from parent wrapper */}
                      <span className="flex-1 truncate text-left text-sm font-medium text-black">{room.name}</span>
                      {/* BUG-036: use RadioButton component instead of raw <input type="radio"> */}
                      <RadioButton
                        checked={selectedRoomId === room.roomId}
                        value={room.roomId}
                        onChange={() => setSelectedRoomId(room.roomId)}
                        size="md"
                      />
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* BUG-038: flex-col on mobile (primary on top), flex-row on md+ */}
        {/* BUG-041: pb-12 = 48px bottom padding per Figma spec */}
        <div className="flex flex-col gap-3 px-4 pt-4 pb-12 md:flex-row md:gap-4 md:px-6 md:pt-6 md:pb-12">
          {/* BUG-040: "Use existing" leads (left on desktop / top on mobile) */}
          <Button variant="secondary" onClick={handleUseExisting} disabled={isLoading} className="flex-1">
            {t('schedule_meeting.use_existing_chat')}
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateNew}
            disabled={isLoading}
            loading={isLoading}
            className="flex-1"
          >
            {t('schedule_meeting.create_new_chat')}
          </Button>
        </div>
      </Popup.Container>
    </Popup>
  );
};

const GroupChatConflictModal = create(({ meetingId, conflicts, participantMxids }: GroupChatConflictModalProps) => (
  <GroupChatConflictModalInner meetingId={meetingId} conflicts={conflicts} participantMxids={participantMxids} />
));

export default GroupChatConflictModal;
